import EventEmitter from "eventemitter3";
import { createMicrophoneAudioTrack } from "../client";
import { workletCode } from "./worker/AudioWorklet";
import { create, ConverterType } from '@alexanderolsen/libsamplerate-js';
import { Config } from "../client/types";

const SAMPLE_RATE = 16000;

export class AudioService extends EventEmitter {
  private config: Config;
  private audioContext: AudioContext | null = null;
  private stream: MediaStream | null = null;

  // Chrome
  private audioNode: AudioWorkletNode | null = null;

  // Others
  private captureNode: ScriptProcessorNode | null = null;
  private audioData: Float32Array[] = [];
  private audioDataIndex: number = 0;
  private pause: boolean = false;
  private inputResampler: any | null;
  private outputResampler: any | null;
  private needResample: boolean = false;
  private resamplerCreated: boolean = false;

  constructor(config: Config) {
    super();
    this.config = config;
  }

  private print(message: string) {
    if (this.config.debug) {
      console.log("[millis audio service]", message);
    }
  }

  public async start() {
    const sampleRate = SAMPLE_RATE;
    if (this.isFirefox()) {
      // TODO assume firefox doesn't use audioworklet
      this.audioContext = new AudioContext({ latencyHint: "interactive" });
      this.needResample = true;
    } else {
      this.needResample = false;
      this.audioContext = new AudioContext({ latencyHint: "interactive", sampleRate: sampleRate });
    }
    this.print("starting audio service, firefox: " + this.isFirefox() + ", need resample: " + this.needResample);

    try {
      this.print("requesting microphone permission");
      this.stream = await createMicrophoneAudioTrack(sampleRate, this.config);
      this.print("microphone permission granted");
    } catch (error) {
      this.print("microphone permission denied");
      throw new Error("User didn't give microphone permission");
    }

    this.print("starting audio capture and playback processor, worklet: " + this.isAudioWorkletSupported());
    if (this.isAudioWorkletSupported()) {
      this.print("Starting audio worklet");
      this.audioContext.resume();
      const blob = new Blob([workletCode], { type: "application/javascript" });
      const blobURL = URL.createObjectURL(blob);
      await this.audioContext.audioWorklet.addModule(blobURL);
      this.print("Audio worklet loaded");
      this.audioNode = new AudioWorkletNode(
        this.audioContext,
        "capture-and-playback-processor",
      );
      this.print("Audio worklet setup");

      this.audioNode.port.onmessage = (e) => {
        if (e.data instanceof Uint8Array) {
          this.emit("data", e.data);
        } else if (e.data === "playback_finished") {
          this.onPlaybackFinished();
        }
      };

      const source = this.audioContext.createMediaStreamSource(this.stream);
      source.connect(this.audioNode);
      this.audioNode.connect(this.audioContext.destination);
      const userAnalyser = this.audioContext.createAnalyser();
      source.connect(userAnalyser);
      this.emit("useraudioready", {
        analyser: userAnalyser,
        stream: this.stream
      });

      const analyser = this.audioContext.createAnalyser();
      this.audioNode.connect(analyser);
      analyser.connect(this.audioContext.destination);
      this.emit("analyzer", analyser);
    } else {
      this.print("Starting audio capture node");
      const source = this.audioContext.createMediaStreamSource(this.stream);
      this.captureNode = this.audioContext.createScriptProcessor(2048, 1, 1);
      this.captureNode.onaudioprocess = (audioProcessingEvent: AudioProcessingEvent) => {

        if (this.captureNode && this.audioContext) {
          // Resample inputData
          if (this.needResample && !this.resamplerCreated) {
            let converterType = ConverterType.SRC_SINC_FASTEST;
            let nChannels = 1;
            this.resamplerCreated = true;
            create(nChannels, this.audioContext.sampleRate, sampleRate, {
              converterType: converterType, // default SRC_SINC_FASTEST. see API for more
            }).then((src: any) => {
              this.inputResampler = src;
            });
            create(nChannels, sampleRate, this.audioContext.sampleRate, {
              converterType: converterType, // default SRC_SINC_FASTEST. see API for more
            }).then((src: any) => {
              this.outputResampler = src;
            });
          }
          var inputData = audioProcessingEvent.inputBuffer.getChannelData(0);
          var resampledAudio = null;
          if (this.inputResampler != null) {
            resampledAudio = this.inputResampler.full(inputData);
          } else {
            resampledAudio = inputData;
          }
          const pcmData = convertFloat32ToUint8(resampledAudio);
          this.emit("data", pcmData);

          // Playback here
          const outputBuffer = audioProcessingEvent.outputBuffer;
          const outputChannel = outputBuffer.getChannelData(0);
          for (let i = 0; i < outputChannel.length; ++i) {
            if (this.audioData.length > 0 && !this.pause) {
              outputChannel[i] = this.audioData[0][this.audioDataIndex++];
              if (this.audioDataIndex === this.audioData[0].length) {
                this.audioData.shift();
                this.audioDataIndex = 0;
                if (this.audioData.length == 0) {
                  this.onPlaybackFinished();
                }
              }
            } else {
              outputChannel[i] = 0;
            }
          }
        }
      };
      source.connect(this.captureNode);
      this.captureNode.connect(this.audioContext.destination);
      this.print("Audio capture node setup");
      const userAnalyser = this.audioContext.createAnalyser();
      source.connect(userAnalyser);
      this.emit("useraudioready", {
        analyser: userAnalyser,
        stream: this.stream
      });
      const analyser = this.audioContext.createAnalyser();
      this.captureNode.connect(analyser);
      analyser.connect(this.audioContext.destination);
      this.emit("analyzer", analyser);
    }
  }

  public async stop() {
    this.audioContext?.suspend();
    this.audioContext?.close();

    if (this.isAudioWorkletSupported()) {
      this.audioNode?.disconnect();
      this.audioNode = null;
    } else {
      if (this.captureNode) {
        this.captureNode.disconnect();
        this.captureNode.onaudioprocess = null;
        this.captureNode = null;
        this.audioData = [];
        this.audioDataIndex = 0;
      }
    }
    this.stream?.getTracks().forEach((track) => track.stop());
    this.audioContext = null;
    this.stream = null;
  }

  public play(audio: Uint8Array) {
    if (this.isAudioWorkletSupported()) {
      this.audioNode?.port.postMessage(audio);
    } else {
      const float32Data = convertUint8ToFloat32(audio);
      var resampledAudio = null;
      if (this.outputResampler != null) {
        // Resample output samples
        resampledAudio = this.outputResampler.full(float32Data);
      } else {
        resampledAudio = float32Data;
      }
      this.audioData.push(resampledAudio);
    }
  }

  public setpause(pause: boolean) {
    if (this.isAudioWorkletSupported()) {
      this.audioNode?.port.postMessage(pause ? "pause" : "unpause");
    } else {
      this.pause = pause;
    }
  }

  public reset() {
    if (this.isAudioWorkletSupported()) {
      this.audioNode?.port.postMessage("clear");
    } else {
      this.audioData = [];
      this.audioDataIndex = 0;
    }
  }

  private onPlaybackFinished() {
    this.emit("playback_finished");
  }

  private isAudioWorkletSupported(): boolean {
    return /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
  }
  private isFirefox(): boolean {
    return /Firefox/.test(navigator.userAgent);
  }
}

export function convertUint8ToFloat32(array: Uint8Array): Float32Array {
  const targetArray = new Float32Array(array.byteLength / 2);

  // A DataView is used to read our 16-bit little-endian samples out of the Uint8Array buffer
  const sourceDataView = new DataView(array.buffer);

  // Loop through, get values, and divide by 32,768
  for (let i = 0; i < targetArray.length; i++) {
    targetArray[i] = sourceDataView.getInt16(i * 2, true) / Math.pow(2, 16 - 1);
  }
  return targetArray;
}

export function convertFloat32ToUint8(array: Float32Array): Uint8Array {
  const buffer = new ArrayBuffer(array.length * 2);
  const view = new DataView(buffer);

  for (let i = 0; i < array.length; i++) {
    const value = (array[i] as number) * 32768;
    view.setInt16(i * 2, value, true); // true for little-endian
  }

  return new Uint8Array(buffer);
}